import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '../lib/api';
import { 
  Newspaper, 
  Package, 
  Settings, 
  CreditCard,
  FileText,
  Plus,
  TrendingUp,
  Users,
  Eye,
  Calendar,
  ArrowRight,
  Star,
  Activity
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Fetch dashboard stats
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardApi.getStats().then((res: any) => res.data.data),
  });

  // Fetch recent activity
  const { data: recentActivity, isLoading: activityLoading } = useQuery({
    queryKey: ['dashboard-activity'],
    queryFn: () => dashboardApi.getRecentActivity().then((res: any) => res.data.data),
  });

  // Stats data from API
  const stats = statsData ? [
    {
      title: t('totalNews'),
      value: statsData.news.total.toString(),
      change: `${statsData.news.published}`,
      trend: 'up',
      icon: Newspaper,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      title: t('totalProducts'),
      value: statsData.products.total.toString(),
      change: `${statsData.products.with_arabic}`,
      trend: 'up',
      icon: Package,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      title: t('totalServices'),
      value: statsData.services.total.toString(),
      change: `${statsData.services.with_arabic}`,
      trend: 'up',
      icon: Settings,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
  ] : [];

  const quickActions = statsData ? [
    {
      title: t('news'),
      description: t('manageNews'),
      href: '/news',
      icon: Newspaper,
      gradient: 'from-blue-500 to-blue-600',
      count: statsData.news.total,
    },
    {
      title: t('products'),
      description: t('manageProducts'),
      href: '/products',
      icon: Package,
      gradient: 'from-green-500 to-green-600',
      count: statsData.products.total,
    },
    {
      title: t('services'),
      description: t('manageServices'),
      href: '/services',
      icon: Settings,
      gradient: 'from-purple-500 to-purple-600',
      count: statsData.services.total,
    },
    {
      title: t('newsCards'),
      description: t('manageNewsCards'),
      href: '/news-cards',
      icon: FileText,
      gradient: 'from-orange-500 to-orange-600',
      count: statsData.news_cards.total,
    },
    {
      title: t('serviceCards'),
      description: t('manageServiceCards'),
      href: '/service-cards',
      icon: CreditCard,
      gradient: 'from-red-500 to-red-600',
      count: statsData.service_cards.total,
    },
  ] : [];

  // Transform API recent activity data
  const recentActivities = recentActivity ? recentActivity.map((activity: any) => ({
    action: activity.type === 'news' ? t('newArticlePublished') : 
            activity.type === 'product' ? t('productUpdated') :
            activity.type === 'service' ? t('serviceAdded') : t('itemUpdated'),
    target: i18n.language === 'ar' ? (activity.title_ar || activity.title) : activity.title,
    time: new Date(activity.created_at).toLocaleDateString(),
    icon: activity.type === 'news' ? Newspaper :
          activity.type === 'product' ? Package :
          activity.type === 'service' ? Settings : FileText,
    color: 'text-primary',
  })) : [];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col space-y-2">
        <h1 className="text-4xl font-bold text-foreground">{t('dashboard')}</h1>
        <p className="text-muted-foreground text-lg">
          {t('welcomeBack')}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {statsLoading ? (
          // Loading skeleton
          Array.from({ length: 3 }).map((_, index) => (
            <Card key={index} className="relative overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                    <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                  </div>
                  <div className="p-3 rounded-full bg-gray-200 animate-pulse">
                    <div className="h-6 w-6"></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          stats.map((stat, index) => (
          <Card key={index} className="relative overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                  <p className="text-3xl font-bold text-foreground">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-full ${stat.bgColor}`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
          ))
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Quick Actions */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                {t('contentManagement')}
              </CardTitle>
              <CardDescription>
                {t('quickAccess')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {statsLoading ? (
                  // Loading skeleton for quick actions
                  Array.from({ length: 6 }).map((_, index) => (
                    <Card key={index} className="animate-pulse">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="p-3 rounded-xl bg-gray-200">
                              <div className="h-6 w-6"></div>
                            </div>
                            <div className="space-y-2">
                              <div className="h-4 bg-gray-200 rounded w-20"></div>
                              <div className="h-3 bg-gray-200 rounded w-32"></div>
                            </div>
                          </div>
                          <div className="h-6 w-6 bg-gray-200 rounded"></div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  quickActions.map((action, index) => (
                  <Link key={index} to={action.href}>
                    <Card className="group hover:shadow-lg transition-all duration-300 cursor-pointer border-2 hover:border-primary/20">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className={`p-3 rounded-xl bg-gradient-to-r ${action.gradient} text-white shadow-lg`}>
                              <action.icon className="h-6 w-6" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors p-2">
                                {action.title}
                              </h3>
                              <p className="text-sm text-muted-foreground pr-2">{action.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge variant="secondary" className="text-xs">
                              {action.count}
                            </Badge>
                            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {t('recentActivity')}
              </CardTitle>
              <CardDescription>
                {t('latestUpdates')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {activityLoading ? (
                  // Loading skeleton for recent activities
                  Array.from({ length: 5 }).map((_, index) => (
                    <div key={index} className="flex items-start space-x-3 animate-pulse">
                      <div className="p-2 rounded-full bg-gray-200">
                        <div className="h-4 w-4"></div>
                      </div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                        <div className="h-3 bg-gray-200 rounded w-24"></div>
                        <div className="h-3 bg-gray-200 rounded w-16"></div>
                      </div>
                    </div>
                  ))
                ) : (
                  recentActivities.map((activity: any, index: number) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="p-2 rounded-full bg-primary/10">
                      <activity.icon className={`h-4 w-4 ${activity.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">
                        {activity.action}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {activity.target}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {activity.time}
                      </p>
                    </div>
                  </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quick Create Actions */}
      {/* <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            {t('quickCreate')}
          </CardTitle>
          <CardDescription>
            {t('createContent')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Link to="/news/create">
              <Button variant="outline" className="w-full h-24 flex flex-col items-center justify-center gap-3 hover:bg-accent hover:text-accent-foreground hover:border-primary/20 transition-all">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Newspaper className="h-6 w-6 text-primary" />
                </div>
                <span className="text-sm font-medium">{t('addNews')}</span>
              </Button>
            </Link>
            <Link to="/products/create">
              <Button variant="outline" className="w-full h-24 flex flex-col items-center justify-center gap-3 hover:bg-accent hover:text-accent-foreground hover:border-primary/20 transition-all">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Package className="h-6 w-6 text-primary" />
                </div>
                <span className="text-sm font-medium">{t('addProduct')}</span>
              </Button>
            </Link>
            <Link to="/services/create">
              <Button variant="outline" className="w-full h-24 flex flex-col items-center justify-center gap-3 hover:bg-accent hover:text-accent-foreground hover:border-primary/20 transition-all">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Settings className="h-6 w-6 text-primary" />
                </div>
                <span className="text-sm font-medium">{t('addService')}</span>
              </Button>
            </Link>
            <Link to="/news-cards/create">
              <Button variant="outline" className="w-full h-24 flex flex-col items-center justify-center gap-3 hover:bg-accent hover:text-accent-foreground hover:border-primary/20 transition-all">
                <div className="p-2 rounded-lg bg-primary/10">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <span className="text-sm font-medium">{t('addNewsCard')}</span>
              </Button>
            </Link>
            <Link to="/service-cards/create">
              <Button variant="outline" className="w-full h-24 flex flex-col items-center justify-center gap-3 hover:bg-accent hover:text-accent-foreground hover:border-primary/20 transition-all">
                <div className="p-2 rounded-lg bg-primary/10">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
                <span className="text-sm font-medium">{t('addServiceCard')}</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card> */}
    </div>
  );
};

export default Dashboard;
